import React, { useEffect, useState } from 'react';
import {
  NavLink, Redirect, Route, Switch, withRouter,
} from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import { Collapse } from '@blueprintjs/core';
import isObject from 'lodash.isobject';
import i18n from 'i18n';
import * as warrantActions from 'xcipio/warrants/warrant-actions';
import CustomBreadcrumbs from 'components/CustomBreadcrumbs';
import {
  getEnabledAccessFunction,
  getEnabledFeatures,
  isMultitenantEnabled,
} from 'data/supportedFeatures/supportedFeatures.selectors';
import {
  getDiscoverySettingBackupserviceUrl,
  isAlarmFeatureEnabled,
  isAlarmNotificationProfileEnabled,
} from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { SupportedFeaturesAccessFunctions } from 'data/supportedFeatures/supportedFeatures.types';
import { RootState } from 'data/root.reducers';
import cloneDeep from 'lodash.clonedeep';
import AuthenticationModule from './authentication';
import navbarConfig from './shared/navbar/navbar-config';
import AuthUtils from './authentication/auth-utils';
import { Projects } from './navbarItems';
import DownIcon from './assets/downIcon.png';
import RightIcon from './assets/rightIcon.png';
import './routes.scss';
import {
  SYSTEM,
  SYSTEM_CONFIGURATION_ROUTE,
  SIP_SETTING,
  REGISTERED_NODES,
  BACKUP_RESTORE,
  SECURITY,
  MISCELLANEOUS,
  FILTER_SETTING,
  TARGET_TEMPLATE,
  NETWORK,
  XDP,
  XTP,
  FQDN,
  XTP_XDP_CONNECTION,
  RADIUS_PARSER_OPTIONS,
  RADIUS_PARSER_PASSWORD,
  NAT_CONFIGURATION_ROUTE,
  NETWORK_ID_ROUTE,
  TARGET_DOMAIN,
  DIALED_DIGIT_EXTRACTION_SERVER_ROUTE,
  CF_GROUP_ROUTE,
  CSP_CONFIG_ROUTE,
  DISTRIBUTORS_ROUTE,
  IRI_BUFFERING_ROUTE,
  PERIODIC_AUDIT_ROUTE,
  PROCESS_OPTIONS_ROUTE,
  TLS_PROFILE,
  JURISDICTION_AREA,
  CF_FTP_INFO_ROUTE,
  UTIMACO_PACKET_SWITCH_ROUTE,
  UTIMACO_CIRCUIT_SWITCH_ROUTE,
  FILTER_GROUP,
  FILTERS,
  AF_FILTER,
  TARGET_DELIVERY_TEMPLATE_PER_ACCESS_FUNCTION,
  SSH_SECURITY_INFORMATION,
  MEDIA_GATEWAY,
  ALARM_NOTIFICATION_PROFILE,
  AFWHITELIST,
  XCCA,
  XCCA_DASHBOARD,
  SNMP_USERS,
  SNMP_DEST,
  CONTACT,
} from './constants';

interface SideBarProps {
  enabledAccessFunctions: SupportedFeaturesAccessFunctions,
  enabledBackup: boolean
  isAlarmFeatureEnabled?: boolean,
  isAlarmNotificationProfileEnabled?: boolean,
  hasCspAdminPrivilege?: boolean,
}

interface subMenuItem {
  navName: string
  navRoute: string
  hideTitle?: boolean
}
interface menuItem {
  moduleNav: string
  items: subMenuItem[]
}

interface Titles {
  moduleNav: string
  items: subMenuItem[]
}

const menuItems: menuItem[] = [
  {
    moduleNav: i18n.t('audit.userAudit.grid.network'),
    items: [
      // {
      //   navName: 'Delivery Function Network Interface',
      //   navRoute: '/systemConfiguration/dummy-link-network',
      // },
      // {
      //   navName: 'Domain',
      //   navRoute: '/systemConfiguration/dummy-link-domain',
      // },
      {
        navName: i18n.t('common.menu.fqdn'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${FQDN}`,
      },
      {
        navName: i18n.t('natConfiguration.create.title'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${NAT_CONFIGURATION_ROUTE}`,
      },
      {
        navName: i18n.t('networkId.breadCrumb'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${NETWORK_ID_ROUTE}`,
      },
      {
        navName: i18n.t('common.menu.radiusParserOptions'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${RADIUS_PARSER_OPTIONS}`,
      },
      {
        navName: i18n.t('common.menu.radiusParserPassword'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${RADIUS_PARSER_PASSWORD}`,
        hideTitle: true,
      },
      {
        navName: i18n.t('common.menu.xtp'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${XTP}`,
        hideTitle: true,
      },
      {
        navName: i18n.t('common.menu.xdp'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${XDP}`,
      },
      {
        navName: i18n.t('common.menu.xtpXdpConnection'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${XTP_XDP_CONNECTION}`,
      },
      {
        navName: i18n.t('common.menu.targetDomain'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${TARGET_DOMAIN}`,
      },
      {
        navName: i18n.t('common.menu.snmpDestinations'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${SNMP_DEST}`,
      },
    ],
  },
  {
    moduleNav: i18n.t('common.menu.system'),
    items: [
      {
        navName: i18n.t('dialedDigitExtractionServer.breadCrumb'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${DIALED_DIGIT_EXTRACTION_SERVER_ROUTE}`,
      },
      {
        navName: i18n.t('common.menu.collectionFunctionGroups'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${CF_GROUP_ROUTE}`,
      },
      {
        navName: i18n.t('common.menu.cspConfigs'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${CSP_CONFIG_ROUTE}`,
      },
      {
        navName: i18n.t('common.menu.distributors'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${DISTRIBUTORS_ROUTE}`,
      },
      {
        navName: i18n.t('common.menu.afwhitelist'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${AFWHITELIST}`,
      },
      {
        navName: i18n.t('common.menu.iriBuffering'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${IRI_BUFFERING_ROUTE}`,
      },
      {
        navName: i18n.t('alarmNotificationProfile.breadCrumb'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${ALARM_NOTIFICATION_PROFILE}`,
      },
      {
        navName: i18n.t('common.menu.periodicAfAudit'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${PERIODIC_AUDIT_ROUTE}`,
      },
      {
        navName: i18n.t('processOptions.breadCrumb'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${PROCESS_OPTIONS_ROUTE}`,
      },
      {
        navName: i18n.t('common.menu.sipSetting'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${SIP_SETTING}`,
        hideTitle: true,
      },
      {
        navName: i18n.t('common.menu.registeredNodes'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${REGISTERED_NODES}`,
        hideTitle: true,
      },
      {
        navName: i18n.t('common.menu.backupRestore'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${BACKUP_RESTORE}`,
      },
      {
        navName: i18n.t('mediaGateway.breadCrumb'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${MEDIA_GATEWAY}`,
      },
    ],
  },
  {
    moduleNav: i18n.t('common.menu.security'),
    items: [
      {
        navName: i18n.t('tlsProfile.breadCrumb'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${SECURITY}${TLS_PROFILE}`,
      },
      {
        navName: i18n.t('common.menu.sshSecurityInfo'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${SECURITY}${SSH_SECURITY_INFORMATION}`,
      },
      {
        navName: i18n.t('common.menu.snmpUsers'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${SECURITY}${SNMP_USERS}`,
      },
      // {
      //   navName: 'Security Gateway',
      //   navRoute: '/systemConfiguration/dummy-link-security',
      // },
    ],
  },
  {
    moduleNav: i18n.t('common.menu.miscellaneous'),
    items: [
      {
        navName: i18n.t('jurisdictionArea.breadcrumb'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${JURISDICTION_AREA}`,
      },
      {
        navName: i18n.t('cfFtpInfo.breadCrumb'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${CF_FTP_INFO_ROUTE}`,
      },
      {
        navName: i18n.t('common.menu.utimacoPacketSwitch'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${UTIMACO_PACKET_SWITCH_ROUTE}`,
      },
      {
        navName: i18n.t('common.menu.utimacoCircuitSwitch'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${UTIMACO_CIRCUIT_SWITCH_ROUTE}`,
      },
      {
        navName: i18n.t('common.menu.contact'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${CONTACT}`,
      },
    ],
  },
  {
    moduleNav: i18n.t('filterSetting.breadCrumb'),
    items: [
      {
        navName: i18n.t('common.menu.filterGroup'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${FILTER_GROUP}`,
      },
      {
        navName: i18n.t('agGrid.filters'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${FILTERS}`,
      },
      {
        navName: i18n.t('afFilter.breadCrumb'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${AF_FILTER}`,
      },
    ],
  },
  {
    moduleNav: i18n.t('audit.userAudit.grid.targetTempBreadCrum'),
    items: [
      {
        navName: i18n.t('common.menu.targetDeliveryTemplatePerAccessFunction'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${TARGET_TEMPLATE}${TARGET_DELIVERY_TEMPLATE_PER_ACCESS_FUNCTION}`,
      },
      // {
      //   navName: 'Template per Jurisdiction Area',
      //   navRoute: '/systemConfiguration/Jurisdiction-test-Area',
      // },
    ],
  },
  {
    moduleNav: i18n.t('common.menu.xcca'),
    items: [
      {
        navName: i18n.t('xccaDashboard.breadcrumb'),
        navRoute: `${SYSTEM_CONFIGURATION_ROUTE}${XCCA}${XCCA_DASHBOARD}`,
      }
    ],
  },
];

const NoMatch = () => <h2>No matching route.</h2>;

const CollapseMenu = (props: { item: menuItem }) => {
  const [isOpen, setIsOpen] = React.useState(true);
  return (
    <React.Fragment>
      {props.item && props.item.items.length > 0 && (
        <span onClick={() => setIsOpen(!isOpen)} className="buttonMenu">
          <img src={isOpen ? DownIcon : RightIcon} className="imageCollapse" alt="icon" /> {props.item.moduleNav}
        </span>
      )}
      <Collapse isOpen={isOpen}>
        <div className="collapseBody">
          {props.item.items.map((link: subMenuItem, index) => (
            <NavLink key={index} to={link.navRoute} className="menuLabel" activeClassName="active">
              {link.navName}
            </NavLink>
          ))}
        </div>
      </Collapse>
    </React.Fragment>
  );

};

const SideBar = ({
  enabledAccessFunctions, enabledBackup,
  isAlarmFeatureEnabled, isAlarmNotificationProfileEnabled, hasCspAdminPrivilege,
}: SideBarProps) => {
  const state = useSelector((state: RootState) => state);

  useEffect(() => {
    const enabledAccessFunctionsArray = Object.keys(enabledAccessFunctions).map((k) => enabledAccessFunctions[k]);

    const filterTargetTemplate = ({ moduleNav }: menuItem) => {
      const isTargetTemplateItem = moduleNav === 'Target Template';
      const features = getEnabledFeatures(state);
      const hasTargetTemplateAcess = features.includes('AF-RIBBONC20-CSC-X1')
        || features.includes('AF-UTIMACO')
        || features.includes('AF-ERICSSONLIIMS-WS')
        || (features.includes('AF-GENBANDC20')
          && features.includes('AF-NTILI'));

      return !isTargetTemplateItem || hasTargetTemplateAcess;
    }

    const cloneMenuItems = cloneDeep(menuItems);
    let searchMenuItems = cloneMenuItems.map((m) => {
      if (enabledAccessFunctionsArray.filter((f) => f.features.includes('AF-UTIMACO')).length === 0 && m.moduleNav === 'Miscellaneous') {
        m.items = m.items.filter((i) => i.navRoute !== '/systemConfiguration/miscellaneous/utmpktcfcis');
      }

      return m;
    }).filter(filterTargetTemplate);

    if (!enabledBackup) {
      searchMenuItems = searchMenuItems.map((m) => {
        m.items = m.items.filter((i) => i.navRoute !== `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${BACKUP_RESTORE}`);
        return m;
      })
    }

    if (!isAlarmFeatureEnabled || !isAlarmNotificationProfileEnabled) {
      searchMenuItems = searchMenuItems.map((m) => {
        if (m.moduleNav === i18n.t('common.menu.system')) {
          m.items = m.items.filter((i) => i.navRoute !== `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${ALARM_NOTIFICATION_PROFILE}`)
        }
        return m;
      })
    }

    if (!hasCspAdminPrivilege) {
      searchMenuItems = searchMenuItems.map((m) => {
        if (m.moduleNav === i18n.t('common.menu.system')) {
          m.items = m.items.filter((i) => i.navRoute !== `${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${CSP_CONFIG_ROUTE}`)
        }
        return m;
      })
    }

    setBaseMenuList(searchMenuItems);
    setSearchMenuList(searchMenuItems);

  }, [
    enabledAccessFunctions, enabledBackup, isAlarmFeatureEnabled, isAlarmNotificationProfileEnabled,
    hasCspAdminPrivilege,
  ]);

  const [search, setSearch] = useState('');
  // The menu list with the disabled features already filtered out, used as the source for the search.
  const [baseMenuList, setBaseMenuList] = useState([...menuItems]);
  const [searchMenuList, setSearchMenuList] = useState([...menuItems]);

  const handleSearch = (e: React.FormEvent<HTMLInputElement>) => {
    setSearch(e.currentTarget.value);
    const searchList = JSON.parse(JSON.stringify(baseMenuList));
    const nList = searchList.map((list: menuItem) => {
      const n = list.items?.filter(
        (item: subMenuItem) => !item.navName.toLowerCase().search(new RegExp(e.currentTarget.value.toLowerCase())),
      );
      return { items: n, moduleNav: list.moduleNav };
    });
    setSearchMenuList(search === '' ? baseMenuList : nList);
  };

  return (
    <div className="padding">
      <h1 className="leftMenuHeading">System Configuration</h1>

      <div className="bp3-input-group">
        <input type="text" name="login" className="search" placeholder="Search" onChange={(e) => handleSearch(e)} />
      </div>

      {searchMenuList.map((item, index) => (
        <CollapseMenu key={index} item={item} />
      ))}
    </div>
  );
};
type AuthenticatedRouteProps<T> = T & { component: React.Component };
const AuthenticatedRoute = ({
  component: Component, routePermissions, enabledAccessFunctions,
  deliveryFunctionId, backupserviceUrl, isAlarmFeatureEnabled, isAlarmNotificationProfileEnabled,
  getSupportedFeature, ...rest
}: AuthenticatedRouteProps<any>) => {

  useEffect(() => {
    getSupportedFeature(deliveryFunctionId);

  }, [getSupportedFeature, deliveryFunctionId]);

  const [showDropShadow, setShowDropShadow] = useState(false);
  const url = window.location.pathname;
  const titles = menuItems
    .map((list) => {
      const n = list.items.filter((item) => item.navRoute === url);
      return n.length > 0 && { items: n, moduleNav: list.moduleNav };
    })
    .filter(isObject)[0];

  const showBreadcrumbs = url.indexOf('systemConfiguration/') > -1;

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (event.currentTarget.scrollTop > 0) {
      setShowDropShadow(true);
    } else {
      setShowDropShadow(false);
    }
  };

  const getClassName = (): string => {
    if (url === '/systemConfiguration/xms') {
      return 'grid-outer-container-added-settings';
    }

    return 'grid-outer-container';
  };

  const renderTitleHeader = (title: string, hideTitle?: boolean) => {
    if (hideTitle) {
      return null;
    }

    return (
      <p className="titleHeader">{title}</p>
    )
  }

  return (
    <Route
      {...rest}
      render={(props: any) => {
        if (rest.isAuthenticated && AuthUtils.hasPermissions(routePermissions, rest.privileges)) {
          return (
            <React.Fragment>
              {window.location.pathname.indexOf('systemConfiguration') > -1 ? (
                <div
                  className="row"
                  id="system-configuration"
                >
                  {url !== '/systemConfiguration/xms' && (
                    <div className="sideBarBlock">
                      <SideBar
                        enabledAccessFunctions={enabledAccessFunctions}
                        enabledBackup={backupserviceUrl !== ''}
                        isAlarmFeatureEnabled={isAlarmFeatureEnabled}
                        isAlarmNotificationProfileEnabled={isAlarmNotificationProfileEnabled}
                        hasCspAdminPrivilege={AuthUtils.hasPermissions(['csp_admin'], rest.privileges)}
                      />
                    </div>
                  )}
                  <div className="page-container">
                    <React.Fragment>
                      <div className={`page-header ${showDropShadow ? 'drop-shadow' : ''}`}>
                        {(showBreadcrumbs && url !== '/systemConfiguration/xms') && (
                          <CustomBreadcrumbs rootPage="settings" />
                        )}
                      </div>
                    </React.Fragment>
                    <div onScroll={handleScroll} className={`${getClassName()}`}>
                      {renderTitleHeader((titles as Titles)?.items[0]?.navName, (titles as Titles)?.items[0]?.hideTitle)}
                      <Component {...props} />
                    </div>
                  </div>
                </div>
              ) : (
                <Component {...props} />
              )}
            </React.Fragment>

          );
        }

        if (!rest.isAuthenticated) {
          window.location.replace(`${window.location.origin}/authentication/login`);
          return (
            <Redirect
              to={{
                pathname: '/authentication/login',
              }}
            />
          );
        }
        return null;
      }}
    />
  );
};

const mapStateToProps = (state: any) => ({
  isAuthenticated: state.authentication.isAuthenticated,
  privileges: state.authentication.privileges,
  enabledAccessFunctions: getEnabledAccessFunction(state),
  deliveryFunctionId: state.global.selectedDF,
  backupserviceUrl: getDiscoverySettingBackupserviceUrl(state),
  isAlarmFeatureEnabled: isAlarmFeatureEnabled(state),
  isAlarmNotificationProfileEnabled: isAlarmNotificationProfileEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserName(state),
});

const mapDispatch = {
  getSupportedFeature: warrantActions.getWarrantFormConfig,
};

const ConnectedAuthenticatedRoute = connect(mapStateToProps, mapDispatch)(AuthenticatedRoute);

const RootRoutes = (props: any) => {
  const navBarItemToPrivileges = navbarConfig(
    props.isExternalDashboardEnabled,
    props.isSecurityGatewayEnabled,
    props.isXmsMenusEnabled,
    props.isNetworkTopologyEnabled,
    props.isAlarmFeatureEnabled,
    props.isAlarmNotificationProfileEnabled,
    props.externalDashboardId,
    props.systemHealthDashboardId,
    props.aggLicensesDashboardId,
    props.xcipioLicensesDashboardId,
    props.openSearchUrl,
    props.userId,
    props.isEWarrantEnabled,
    props.isMultitenantEnabled,
  ).reduce((accumulator: any, navItem: any) => {
    accumulator[navItem.id] = navItem.permissions;
    if (navItem.children) {
      for (let index = 0; index < navItem.children.length; index += 1) {
        accumulator[navItem.children[index].id] = navItem.children[index].permissions;
      }
    }
    return accumulator;
  }, {});

  const getClassName = (): string => {
    const {
      location: { pathname },
    } = props;

    if (pathname === '/systemConfiguration/system/dipms' || pathname.includes('external/')) {
      return 'external-apps-container';
    }

    if (pathname === '/systemConfiguration/xms') {
      return 'external-apps-container-add-settings';
    }

    if (pathname.includes('/cfs')) {
      return 'app-container-no-sub-menu';
    }

    return '';
  };

  return (
    <div className={`app-container ${getClassName()}`} style={{ paddingTop: window.location.pathname.indexOf('systemConfiguration') > -1 ? 85 : 103 }}>
      <Switch>
        <Route exact path="/authentication*" component={AuthenticationModule} />
        {Projects.map((project: any, index: number) => {
          return (
            <ConnectedAuthenticatedRoute
              key={index}
              {...props}
              path={project.path}
              component={project.component}
              routePermissions={navBarItemToPrivileges[project.permissions]}
            />
          );
        })}

        <Redirect from="/" to="/authentication/login" />
        <Route component={NoMatch} />
      </Switch>
    </div>
  );
};

const mapSettingsToPropsRootRoutes = ({
  discoveryXcipio: {
    discoveryXcipioSettings: {
      isExternalDashboardEnabled, isSecurityGatewayEnabled, isXmsMenusEnabled, isNetworkTopologyEnabled,
      isAlarmFeatureEnabled,
      isAlarmNotificationProfileEnabled,
      xcEwarrantEnable,
      externalDashboardId,
      systemHealthDashboardId,
      aggLicensesDashboardId,
      xcipioLicensesDashboardId,
      openSearchUrl
    },
  },
  authentication: {
    userId,
  },
}: any) => ({
  isExternalDashboardEnabled,
  isSecurityGatewayEnabled,
  isXmsMenusEnabled,
  isNetworkTopologyEnabled,
  isAlarmFeatureEnabled,
  isAlarmNotificationProfileEnabled,
  isEWarrantEnabled: xcEwarrantEnable ?? false,
  externalDashboardId,
  systemHealthDashboardId,
  aggLicensesDashboardId,
  xcipioLicensesDashboardId,
  openSearchUrl,
  userId,
});

const mapStateToPropsRootRoutes = (state: any) => ({
  ...mapSettingsToPropsRootRoutes(state),
  isMultitenantEnabled: isMultitenantEnabled(state),
});

// do not attempt to connect the store here
export default withRouter(connect(mapStateToPropsRootRoutes)(RootRoutes));
